import {
  type Component,
  Show,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import { transcribeAudio } from "@/api/transcribe";

import { shouldStartPushToTalk, shouldStopPushToTalk } from "./pushToTalkModel";
import { MicRecorder, filenameFor } from "./recorder";

interface VoiceRecorderProps {
  // True while a modal overlay (⌘K palette / `?` cheat sheet) is open — the
  // push-to-talk chord defers to it. Read live.
  isBlocked: () => boolean;
  // Called with the transcript once a take is decoded. The composer appends it.
  onTranscript: (text: string) => void;
}

const ERROR_TTL_MS = 4000;
// Safety cap so a forgotten tap-to-toggle recording (mobile) can't run
// forever. Well under the sidecar's 120 s decode cap.
const MAX_RECORDING_MS = 60_000;

// Coarse pointer = touch. Hold-to-talk is a desktop gesture that fights the
// browser's long-press (selection/callout), so on touch we tap-to-toggle.
const isCoarse = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(pointer: coarse)")?.matches ?? false);

/**
 * Mic button + voice push-to-talk. Desktop: press-and-hold the button (or hold
 * Option/Alt) → record, release → transcribe. Touch: tap to start, tap to stop
 * (a max-duration timer backstops a forgotten recording). Owns its own
 * transient error state so a permission denial / sidecar outage shows inline
 * without bubbling to the chat error surface. Nothing is stored — the Blob is
 * POSTed and dropped.
 */
const VoiceRecorder: Component<VoiceRecorderProps> = (props) => {
  const [recording, setRecording] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const mic = new MicRecorder();
  let errorTimer: ReturnType<typeof setTimeout> | undefined;
  let recordTimer: ReturnType<typeof setTimeout> | undefined;

  const flashError = (msg: string) => {
    setError(msg);
    if (errorTimer) clearTimeout(errorTimer);
    errorTimer = setTimeout(() => setError(null), ERROR_TTL_MS);
  };

  const start = async () => {
    if (recording() || busy() || props.isBlocked()) return;
    setError(null);
    try {
      await mic.start();
      setRecording(true);
      recordTimer = setTimeout(() => {
        if (recording()) void stop();
      }, MAX_RECORDING_MS);
    } catch (e) {
      flashError(
        e instanceof DOMException && e.name === "NotAllowedError"
          ? "microphone access denied"
          : "could not start recording",
      );
    }
  };

  const stop = async () => {
    if (!recording()) return;
    if (recordTimer) {
      clearTimeout(recordTimer);
      recordTimer = undefined;
    }
    setRecording(false);
    setBusy(true);
    try {
      const blob = await mic.stop();
      if (blob.size === 0) return;
      const { text } = await transcribeAudio(blob, filenameFor(mic.mimeType));
      if (text.trim()) props.onTranscript(text);
    } catch (e) {
      flashError(transcribeErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // Touch tap-to-toggle.
  const toggle = () => {
    if (recording()) void stop();
    else void start();
  };

  // Global push-to-talk: hold Option/Alt. Alt isn't a printable key, so it
  // never inserts characters in the focused composer. preventDefault on the
  // Alt-down we act on suppresses the browser's menu-bar focus behavior.
  onMount(() => {
    const onDown = (e: KeyboardEvent) => {
      if (
        shouldStartPushToTalk(e, {
          recording: recording(),
          blocked: props.isBlocked() || busy(),
        })
      ) {
        e.preventDefault();
        void start();
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (shouldStopPushToTalk(e, recording())) {
        e.preventDefault();
        void stop();
      }
    };
    // If focus leaves the window mid-hold (e.g. Alt+Tab), keyup never fires —
    // stop here so recording can't wedge on.
    const onBlur = () => {
      if (recording()) void stop();
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", onBlur);
    onCleanup(() => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", onBlur);
    });
  });

  onCleanup(() => {
    if (errorTimer) clearTimeout(errorTimer);
    if (recordTimer) clearTimeout(recordTimer);
    mic.cancel();
  });

  return (
    <>
      <Show when={error()}>
        <output class="neuro-mic-error">{error()}</output>
      </Show>
      <button
        type="button"
        class={recording() ? "neuro-mic is-recording" : "neuro-mic"}
        aria-label={
          recording()
            ? isCoarse()
              ? "recording — tap to stop"
              : "recording — release to transcribe"
            : isCoarse()
              ? "tap to dictate"
              : "hold to dictate (Option/Alt)"
        }
        title={
          busy()
            ? "transcribing…"
            : isCoarse()
              ? "tap to dictate"
              : "hold to dictate · Option/Alt"
        }
        aria-pressed={recording()}
        disabled={busy()}
        // Touch: tap-to-toggle via onClick (hold-to-talk fights the browser's
        // long-press). Desktop fine pointer: press-and-hold via pointer events.
        onPointerDown={(e) => {
          if (isCoarse()) return;
          e.preventDefault();
          void start();
        }}
        onPointerUp={(e) => {
          if (isCoarse()) return;
          e.preventDefault();
          void stop();
        }}
        onPointerLeave={() => {
          if (!isCoarse() && recording() && !busy()) void stop();
        }}
        onClick={() => {
          if (isCoarse()) toggle();
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <Show
          when={!busy()}
          fallback={<span class="neuro-mic-dot" aria-hidden="true" />}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </Show>
      </button>
    </>
  );
};

function transcribeErrorMessage(e: unknown): string {
  if (e && typeof e === "object" && "code" in e) {
    const code = (e as { code?: unknown }).code;
    if (code === "UpstreamUnavailable")
      return "transcription service unavailable";
  }
  return "transcription failed";
}

export default VoiceRecorder;
